import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';

interface Team {
  id: string;
  name: string;
}

const CreateMatch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionData = location.state?.session;

  const [mode, setMode] = useState<'anonymous' | 'teams'>('anonymous');
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [leftTeam, setLeftTeam] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [anonymousSide, setAnonymousSide] = useState<'left' | 'right'>('left');

  const tileBase = 'p-6 rounded-2xl border-2 transition-all duration-300 text-left';
  const tileActive = 'bg-white shadow-card-hover scale-[1.02]';
  const tileInactive = 'bg-white/80 border-gray-200 hover:border-gray-300 hover:shadow-md';

  useEffect(() => {
    if (!sessionData) {
      navigate('/join-session');
      return;
    }

    const ensureSessionActive = async () => {
      try {
        const response = await api.get('/sessions/active');
        if (!response.data || response.data.id !== sessionData.id) {
          navigate('/join-session', { replace: true });
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          navigate('/join-session', { replace: true });
        } else {
          console.error('Failed to validate session', err);
        }
      }
    };

    const fetchTeams = async () => {
      try {
        const mineRes = await api.get('/teams/mine');
        const mine = mineRes.data || [];
        setMyTeams(mine);
      } catch (error) {
        console.error('Failed to fetch teams:', error);
      }
    };

    ensureSessionActive();
    fetchTeams();
  }, [sessionData, navigate]);

  const handleCreateMatch = async () => {
    setError('');
    setLoading(true);

    try {
      let participants: any[];

      if (mode === 'anonymous') {
        participants = [
          { side: anonymousSide },
          { side: anonymousSide === 'left' ? 'right' : 'left' }
        ];
      } else {
        if (!leftTeam) {
          setError('Vyberte svůj tým pro zápas');
          setLoading(false);
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          setError('Je vyžadováno přihlášení');
          setLoading(false);
          return;
        }

        let userId: string | undefined;
        try {
          const payload = token.split('.')[1];
          const decoded: any = JSON.parse(atob(payload));
          userId = decoded.id;
        } catch (err) {
          console.error('Failed to decode token', err);
          setError('Je vyžadováno přihlášení');
          setLoading(false);
          return;
        }

        participants = [
          { userId, teamId: leftTeam, side: 'left' },
        ];
      }

      const response = await api.post('/matches', {
        sessionId: sessionData.id,
        mode: mode === 'anonymous' ? 'casual' : 'competitive',
        participants
      });

      const match = response.data;

      navigate(`/matches/${match.id}`);
    } catch (error: any) {
      console.error('Failed to create match:', error);
      if (error.response?.status === 409) {
        if (error.response?.data?.matchId) {
          navigate(`/matches/${error.response.data.matchId}`);
          return;
        }
        setError(error.response?.data?.message || 'Sezení už není aktivní. Spusťte nové sezení.');
        navigate('/join-session', { replace: true });
        return;
      }

      setError(error.response?.data?.message || 'Zápas se nepodařilo vytvořit. Zkuste to znovu.');
    } finally {
      setLoading(false);
    }
  };

  if (!sessionData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 animate-fade-in">
          <div className="text-6xl sm:text-7xl mb-4 animate-bounce-slow">⚽</div>
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
            Vytvořit zápas
          </h2>
          <p className="text-gray-600 text-lg">Nastavte si zápas a počkejte na soupeře</p>
        </div>

        <div className="bg-white/80 backdrop-blur-lg p-6 rounded-3xl shadow-card-hover border border-white mb-6 animate-slide-up">
          <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Informace o sezení</h3>
          <div className="text-sm">
            <span className="text-gray-600">Kód sezení:</span>
            <p className="font-bold text-primary-600">{sessionData.sessionJoinCode}</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 text-center text-red-700 bg-red-50 rounded-2xl border border-red-200 animate-slide-up">
            {error}
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card-hover border border-white mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <h3 className="text-xl font-bold text-gray-900 mb-6">🎯 Režim zápasu</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => setMode('anonymous')}
              className={`${tileBase} ${
                mode === 'anonymous'
                  ? `border-primary-500 ${tileActive}`
                  : tileInactive
              }`}
            >
              <div className="text-4xl mb-3">🎲</div>
              <h4 className="font-bold text-lg text-gray-900 mb-2">Anonymní</h4>
              <p className="text-sm text-gray-600">Rychlý přátelský zápas bez týmů</p>
            </button>

            <button
              onClick={() => setMode('teams')}
              className={`${tileBase} ${
                mode === 'teams'
                  ? `border-secondary-500 ${tileActive}`
                  : tileInactive
              }`}
            >
              <div className="text-4xl mb-3">👥</div>
              <h4 className="font-bold text-lg text-gray-900 mb-2">S týmy</h4>
              <p className="text-sm text-gray-600">Soutěžní zápas s vašimi týmy</p>
            </button>
          </div>
        </div>

        {mode === 'anonymous' && (
          <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card-hover border border-white mb-6 animate-slide-up" style={{ animationDelay: '0.15s' }}>
            <h3 className="text-xl font-bold text-gray-900 mb-6">🎨 Vyberte barvu</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => setAnonymousSide('left')}
                className={`${tileBase} ${
                  anonymousSide === 'left'
                    ? `border-accent-500 ${tileActive}`
                    : tileInactive
                }`}
              >
                <div className="text-3xl mb-2">🔵</div>
                <div className="font-bold text-lg text-gray-900 mb-1">Modrá strana</div>
                <p className="text-sm text-gray-600">Skóre se zobrazí jako modrý tým</p>
              </button>
              <button
                onClick={() => setAnonymousSide('right')}
                className={`${tileBase} ${
                  anonymousSide === 'right'
                    ? `border-red-500 ${tileActive}`
                    : tileInactive
                }`}
              >
                <div className="text-3xl mb-2">🔴</div>
                <div className="font-bold text-lg text-gray-900 mb-1">Červená strana</div>
                <p className="text-sm text-gray-600">Skóre se zobrazí jako červený tým</p>
              </button>
            </div>
            <p className="mt-4 text-sm text-gray-500">Volba barvy nahrazuje popisek levá/pravá u anonymních zápasů.</p>
          </div>
        )}

        {mode === 'teams' && (
          <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card-hover border border-white mb-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-xl font-bold text-gray-900 mb-6">🏆 Vyberte svůj tým</h3>

            {myTeams.length === 0 ? (
              <div className="text-center p-8 bg-gray-50 rounded-2xl">
                <p className="text-gray-600 mb-4">Zatím nemáte žádné týmy!</p>
                <button
                  onClick={() => navigate('/teams')}
                  className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold rounded-xl hover:shadow-glow transition-all"
                >
                  Vytvořit tým
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    ⬅️ Váš tým
                  </label>
                  <select
                    value={leftTeam}
                    onChange={(e) => setLeftTeam(e.target.value)}
                    className="w-full p-4 bg-white rounded-xl border-2 border-gray-200 focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all"
                  >
                    <option value="">Vyberte tým...</option>
                    {myTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleCreateMatch}
          disabled={loading || (mode === 'teams' && myTeams.length === 0)}
          className="w-full py-5 font-bold text-white text-xl bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl shadow-lg hover:shadow-glow hover:scale-105 transform transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {loading ? 'Vytváření zápasu...' : mode === 'teams' ? 'Vyhlásit výzvu 🚀' : 'Spustit zápas 🚀'}
        </button>
      </div>
    </div>
  );
};

export default CreateMatch;
